import type { Baum, Katalog, KontoInfo, Notiz, NotizZiel, SchluesselInfo, StandInfo, TexturStatus, TodoEintrag, UiVoll, VerlaufGruppe } from "@gui-creator/gemeinsam";
import { useMutation, useQuery, useQueryClient, type QueryKey } from "@tanstack/react-query";
import { api } from "./klient.ts";

export type Konto = { kontoId: string; name: string; rolle: "eigentuemer" | "admin" | "bearbeiter" | "leser"; istClaude: boolean };
export type Status = { eingerichtet: boolean; konto: Konto | null; codeNoetig?: boolean };

export const schluessel = {
  status: ["status"] as const,
  struktur: ["struktur"] as const,
  ui: (id: string) => ["ui", id] as const,
  katalog: (plugin: string) => ["katalog", plugin] as const,
  notizen: (art: NotizZiel["art"], id: string) => ["notizen", art, id] as const,
  todos: (filter: Record<string, string | undefined>) => ["todos", filter] as const,
  texturen: ["texturen"] as const,
  staende: (ui: string) => ["staende", ui] as const,
  verlauf: (ui: string) => ["verlauf", ui] as const,
  konten: ["konten"] as const,
  apiSchluessel: ["api-schluessel"] as const,
};

export const useStatus = () => useQuery({ queryKey: schluessel.status, queryFn: () => api<Status>("/api/status"), staleTime: 60_000 });
export const useStruktur = () => useQuery({ queryKey: schluessel.struktur, queryFn: () => api<Baum>("/api/struktur") });
export const useUi = (id: string | undefined) =>
  useQuery({ queryKey: schluessel.ui(id ?? "-"), queryFn: () => api<UiVoll>(`/api/uis/${id}`), enabled: !!id });
export const useKatalog = (plugin: string | undefined) =>
  useQuery({ queryKey: schluessel.katalog(plugin ?? "-"), queryFn: () => api<Katalog>(`/api/plugins/${plugin}/katalog`), enabled: !!plugin, staleTime: 30_000 });
export const useNotizen = (art: NotizZiel["art"], id: string | undefined) =>
  useQuery({ queryKey: schluessel.notizen(art, id ?? "-"), queryFn: () => api<Notiz[]>(`/api/notizen?art=${art}&id=${id}`), enabled: !!id });
export const useTodos = (filter: Record<string, string | undefined>) =>
  useQuery({
    queryKey: schluessel.todos(filter),
    queryFn: () => api<TodoEintrag[]>(`/api/todos?${new URLSearchParams(Object.entries(filter).filter((e): e is [string, string] => !!e[1]))}`),
  });
export const useStaende = (ui: string | undefined) => useQuery({ queryKey: schluessel.staende(ui ?? "-"), queryFn: () => api<StandInfo[]>(`/api/uis/${ui}/staende`), enabled: !!ui });
export const useVerlauf = (ui: string | undefined) =>
  useQuery({ queryKey: schluessel.verlauf(ui ?? "-"), queryFn: () => api<VerlaufGruppe[]>(`/api/uis/${ui}/verlauf?anzahl=200`), enabled: !!ui });
export const useKonten = (an: boolean) => useQuery({ queryKey: schluessel.konten, queryFn: () => api<KontoInfo[]>("/api/konten"), enabled: an });
export const useApiSchluessel = (an: boolean) => useQuery({ queryKey: schluessel.apiSchluessel, queryFn: () => api<SchluesselInfo[]>("/api/schluessel"), enabled: an });
export const useTexturStatus = () => useQuery({ queryKey: schluessel.texturen, queryFn: () => api<TexturStatus>("/api/texturen/status"), staleTime: 300_000 });

/** Mutation, die nach Erfolg die genannten Abfragen neu lädt. */
export function useAktion<E, A = unknown>(ausfuehren: (eingabe: E) => Promise<A>, neuLaden: QueryKey[] = []) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ausfuehren,
    onSuccess: async () => {
      await Promise.all(neuLaden.map((k) => qc.invalidateQueries({ queryKey: k })));
    },
  });
}
